import { ChevronDownIcon } from '@f8n/icons';
import { styled } from '@f8n-frontend/stitches';
import { VariantProps } from '@stitches/react';
import { useEffect, useState } from 'react';
import { ChangeEvent } from 'react';

import Flex from 'components/base/Flex';
import Text from 'components/base/Text';

export const Select = styled('select', {
  appearance: 'none',
  position: 'absolute',
  top: 0,
  right: 0,
  left: 0,
  bottom: 0,
  width: '100%',
  opacity: 0,
  fontSize: '$2',
  cursor: 'pointer',
});

type SelectWrapperVariants = VariantProps<typeof SelectWrapper>;

const SelectWrapper = styled(Flex, {
  fontFamily: '$body',
  fontWeight: '$semibold',
  fontSize: '$2',
  backgroundColor: '$white100',
  border: '1px solid transparent',
  borderRadius: '$round',
  boxShadow: '$regular0',
  color: '$black100',

  // paddingY: '$3',

  whiteSpace: 'nowrap',
  transition:
    'border-color $1 $ease, box-shadow $1 $ease, background-color $1 $ease, color $1 $ease',
  cursor: 'pointer',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'space-between',

  '@hover': {
    '&:hover': {
      borderColor: '$black100',
      boxShadow: '$regular0, inset 0px 0px 0px 1px $colors$black100',
    },
  },
  '&:active': {
    backgroundColor: '$black5',
    boxShadow: '$regular0, inset 0px 0px 0px 1px $colors$black100',
    transform: 'translate3d(0, 2px, 0)',
  },
  '&:focus-visible': {
    borderColor: '$black100',
    outline: '4px solid $black30',
  },
  '&:disabled': {
    boxShadow: '$regular0',
    color: '$black40',
    '@hover': {
      '&:hover': {
        borderColor: 'transparent',
        boxShadow: '$regular0',
      },
    },
    '&:active': {
      backgroundColor: '$white100',
      transform: 'none',
    },
  },
  variants: {
    size: {
      0: {
        fontSize: '$1',
        paddingX: '$4',
        height: '$formElement0',
      },
      1: {
        fontSize: '$2',
        paddingLeft: '$6',
        paddingRight: '$4',
        height: '$formElement1',
      },
      2: {
        fontSize: '$3',
        paddingLeft: '$7',
        paddingRight: '$6',
        height: '$formElement2',
      },
    },
    isActive: {
      true: {
        borderColor: '$black100',
      },
    },
  },
});

const SelectIcon = styled(Flex, {
  marginLeft: '$2',
  alignItems: 'center',
  alignSelf: 'stretch',
});

export interface SelectOptionConfig {
  id: string;
  label: string;
}

interface SelectFieldProps<T> {
  className?: string;
  defaultSelectedItem: T;
  items: T[];
  onSelectedItemChange: (selectedItem: T) => void;
  selectedItemControlled?: T;
  size?: SelectWrapperVariants['size'];
  disabled?: boolean;
}

export default function SelectField<T extends SelectOptionConfig>(
  props: SelectFieldProps<T>
) {
  const {
    className,
    defaultSelectedItem,
    items = [],
    onSelectedItemChange,
    selectedItemControlled,
    size = 0,
    disabled = false,
  } = props;

  const [selectedItem, setSelectedItem] = useState<T>(defaultSelectedItem);

  const handleChange = (ev: ChangeEvent<HTMLSelectElement>) => {
    const rawValue = ev.target.value;
    const selectedItem = items.find((item) => item.id === rawValue);

    if (selectedItem) {
      setSelectedItem(selectedItem);
      onSelectedItemChange(selectedItem);
    }
  };

  useEffect(() => {
    if (selectedItemControlled) {
      setSelectedItem(selectedItemControlled);
    }
  }, [selectedItemControlled]);

  return (
    <SelectWrapper
      className={className}
      size={size}
      css={{ paddingRight: '$3' }}
    >
      <Select
        disabled={disabled}
        onChange={handleChange}
        value={selectedItem.id}
      >
        {items.map((item, i) => (
          <option key={i} value={item.id}>
            {item.label}
          </option>
        ))}
      </Select>
      <Text css={{ pointerEvents: 'none' }}>{selectedItem.label}</Text>
      <SelectIcon css={{ pointerEvents: 'none' }}>
        <ChevronDownIcon size={0} />
      </SelectIcon>
    </SelectWrapper>
  );
}
